import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { User, AuthError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { clearCache } from '../lib/queryCache'
import SessionTimeoutWarning from '../components/SessionTimeoutWarning'
import {
  ABSOLUTE_TIMEOUT_MS,
  getLastActivityAtKey,
  getSessionStartedAtKey,
  IDLE_TIMEOUT_MS,
  purgeStaleSessionKeys,
  readStoredTimestamp,
  SESSION_EXPIRED_STORAGE_KEY,
  SESSION_WARNING_MS,
} from '../lib/sessionSecurity'

interface Profile {
  id: string
  email: string
  full_name: string | null
  role: 'learner' | 'admin'
  avatar_url: string | null
  primary_org_id: string | null
  login_count: number
  last_login_at: string | null
  created_at: string
}

interface AuthContextValue {
  user: User | null
  profile: Profile | null
  loading: boolean
  profileLoading: boolean
  error: AuthError | null
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null; mfaRequired?: boolean }>
  respondToMfa: (code: string) => Promise<{ error: AuthError | null }>
  signUp: (email: string, password: string, fullName?: string, orgId?: string, phone?: string) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  clearError: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [error, setError] = useState<AuthError | null>(null)
  const [sessionWarningOpen, setSessionWarningOpen] = useState(false)
  const [sessionSecondsRemaining, setSessionSecondsRemaining] = useState(Math.ceil(SESSION_WARNING_MS / 1000))
  const profileRequestRef = useRef(0)
  const activeSessionUserRef = useRef<string | null>(null)
  const sessionWarningOpenRef = useRef(false)
  const warningDeadlineRef = useRef<number | null>(null)
  const warningTimerRef = useRef<number | null>(null)
  const signOutTimerRef = useRef<number | null>(null)
  const countdownIntervalRef = useRef<number | null>(null)
  const lastActivityWriteRef = useRef(0)

  const clearSessionTimers = useCallback(() => {
    if (warningTimerRef.current != null) {
      window.clearTimeout(warningTimerRef.current)
      warningTimerRef.current = null
    }
    if (signOutTimerRef.current != null) {
      window.clearTimeout(signOutTimerRef.current)
      signOutTimerRef.current = null
    }
    if (countdownIntervalRef.current != null) {
      window.clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }
    warningDeadlineRef.current = null
  }, [])

  const clearSessionTracking = useCallback((userId: string | null | undefined) => {
    if (!userId) return

    localStorage.removeItem(getSessionStartedAtKey(userId))
    localStorage.removeItem(getLastActivityAtKey(userId))
  }, [])

  const performLocalSignOut = useCallback(async ({ expired = false }: { expired?: boolean } = {}) => {
    const activeUserId = activeSessionUserRef.current ?? user?.id ?? null

    clearSessionTimers()
    sessionWarningOpenRef.current = false
    setSessionWarningOpen(false)
    setSessionSecondsRemaining(Math.ceil(SESSION_WARNING_MS / 1000))
    clearSessionTracking(activeUserId)

    if (expired) {
      sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, 'true')
    } else {
      sessionStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY)
    }

    activeSessionUserRef.current = null
    setProfile(null)
    setUser(null)
    clearCache()

    try {
      const storageKey = `sb-${new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split('.')[0]}-auth-token`
      localStorage.removeItem(storageKey)
    } catch { /* ignore */ }

    try {
      await supabase.auth.signOut({ scope: 'local' })
    } catch { /* tokens already cleared */ }
  }, [clearSessionTimers, clearSessionTracking, user?.id])

  const syncWarningDeadline = useCallback((userId: string) => {
    const refreshedSessionStartedAt = readStoredTimestamp(localStorage, getSessionStartedAtKey(userId)) ?? Date.now()
    const refreshedLastActivityAt = readStoredTimestamp(localStorage, getLastActivityAtKey(userId)) ?? Date.now()
    const refreshedDeadline = Math.min(
      refreshedLastActivityAt + IDLE_TIMEOUT_MS,
      refreshedSessionStartedAt + ABSOLUTE_TIMEOUT_MS,
    )

    warningDeadlineRef.current = refreshedDeadline
    sessionWarningOpenRef.current = true
    setSessionWarningOpen(true)
    setSessionSecondsRemaining(Math.max(0, Math.ceil((refreshedDeadline - Date.now()) / 1000)))

    if (signOutTimerRef.current != null) {
      window.clearTimeout(signOutTimerRef.current)
      signOutTimerRef.current = null
    }
  }, [])

  const scheduleSessionEnforcement = useCallback((userId: string) => {
    clearSessionTimers()

    const now = Date.now()
    const sessionStartedAt = readStoredTimestamp(localStorage, getSessionStartedAtKey(userId)) ?? now
    const lastActivityAt = readStoredTimestamp(localStorage, getLastActivityAtKey(userId)) ?? now
    const idleDeadline = lastActivityAt + IDLE_TIMEOUT_MS
    const absoluteDeadline = sessionStartedAt + ABSOLUTE_TIMEOUT_MS
    const warningAt = Math.min(idleDeadline - SESSION_WARNING_MS, absoluteDeadline - SESSION_WARNING_MS)
    const logoutAt = Math.min(idleDeadline, absoluteDeadline)

    if (logoutAt <= now) {
      void performLocalSignOut({ expired: true })
      return
    }

    const openWarning = () => {
      syncWarningDeadline(userId)
      countdownIntervalRef.current = window.setInterval(() => {
        if (warningDeadlineRef.current == null) return

        const refreshedSessionStartedAt = readStoredTimestamp(localStorage, getSessionStartedAtKey(userId)) ?? Date.now()
        const refreshedLastActivityAt = readStoredTimestamp(localStorage, getLastActivityAtKey(userId)) ?? Date.now()
        const refreshedDeadline = Math.min(
          refreshedLastActivityAt + IDLE_TIMEOUT_MS,
          refreshedSessionStartedAt + ABSOLUTE_TIMEOUT_MS,
        )

        if (refreshedDeadline !== warningDeadlineRef.current) {
          warningDeadlineRef.current = refreshedDeadline
        }

        const remainingSeconds = Math.max(0, Math.ceil((warningDeadlineRef.current - Date.now()) / 1000))
        setSessionSecondsRemaining(remainingSeconds)

        if (remainingSeconds <= 0 && countdownIntervalRef.current != null) {
          window.clearInterval(countdownIntervalRef.current)
          countdownIntervalRef.current = null
          void performLocalSignOut({ expired: true })
        }
      }, 1000)
    }

    if (warningAt <= now) {
      openWarning()
    } else {
      warningTimerRef.current = window.setTimeout(openWarning, warningAt - now)
    }

    signOutTimerRef.current = window.setTimeout(() => {
      void performLocalSignOut({ expired: true })
    }, logoutAt - now)
  }, [clearSessionTimers, performLocalSignOut, syncWarningDeadline])

  const recordActivity = useCallback((force = false) => {
    const activeUserId = activeSessionUserRef.current
    if (!activeUserId) return

    if (sessionWarningOpen && !force) {
      return
    }

    const now = Date.now()
    if (!force && now - lastActivityWriteRef.current < 5000) {
      return
    }

    lastActivityWriteRef.current = now
    localStorage.setItem(getLastActivityAtKey(activeUserId), String(now))

    if (sessionWarningOpen) {
      sessionWarningOpenRef.current = false
      setSessionWarningOpen(false)
    }

    scheduleSessionEnforcement(activeUserId)
  }, [scheduleSessionEnforcement, sessionWarningOpen])

  const fetchProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    if (error) throw error
    return (data ?? null) as Profile | null
  }, [])

  const loadProfile = useCallback(async (userId: string) => {
    const requestId = ++profileRequestRef.current
    setProfileLoading(true)

    try {
      const nextProfile = await fetchProfile(userId)
      if (profileRequestRef.current === requestId) {
        setProfile(nextProfile)
      }
    } catch {
      // Keep the last known profile on transient refresh failures.
    } finally {
      if (profileRequestRef.current === requestId) {
        setProfileLoading(false)
      }
    }
  }, [fetchProfile])

  useEffect(() => {
    let isMounted = true

    void supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (!isMounted || error) return

        setUser(session?.user ?? null)
        if (!session?.user) {
          profileRequestRef.current += 1
          setProfile(null)
          setProfileLoading(false)
        } else {
          setProfileLoading(true)
        }
      })
      .catch(() => {
        if (!isMounted) return
        profileRequestRef.current += 1
        setProfile(null)
        setProfileLoading(false)
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false)
        }
      })

    // Keep this callback synchronous. Supabase documents that awaiting other
    // client calls here can deadlock subsequent requests during auth events.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return

      const nextUser = session?.user ?? null
      setUser((prev) => {
        if (!nextUser) {
          profileRequestRef.current += 1
          setProfile(null)
          setProfileLoading(false)
        } else if ((prev?.id ?? null) !== nextUser.id) {
          // Different user — pre-emptively flag loading; user.id effect will fetch.
          setProfileLoading(true)
        }
        // Same user (token refresh / tab focus): keep existing profile and loading state.
        return nextUser
      })
      setLoading(false)
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!user?.id) {
      setProfileLoading(false)
      return
    }
    void loadProfile(user.id)
  }, [user?.id, loadProfile])

  // Shared post-authentication steps (used by signIn and the AWS MFA completion).
  const finishLogin = useCallback(async (user: User) => {
    setUser(user)
    void loadProfile(user.id)

    // Check if account is active
    const { data: prof } = await supabase
      .from('profiles')
      .select('is_active')
      .eq('id', user.id)
      .single()
    if (prof && prof.is_active === false) {
      await supabase.auth.signOut({ scope: 'global' })
      const inactiveError = { message: 'Your account has been disabled. Please contact an administrator.', name: 'AuthApiError', status: 403 } as AuthError
      setError(inactiveError)
      return { error: inactiveError }
    }

    // Record login event (fire-and-forget)
    recordLoginEvent(user.id)
    return { error: null }
  }, [loadProfile])

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null)
    const result = await supabase.auth.signInWithPassword({ email, password })
    const { data, error } = result
    if (error) {
      setError(error)
      return { error }
    }
    // AWS optional MFA: Cognito returned a TOTP challenge — finish via respondToMfa.
    if ((result as { mfaRequired?: boolean }).mfaRequired) {
      return { error: null, mfaRequired: true }
    }
    if (data.user) {
      return await finishLogin(data.user)
    }
    return { error: null }
  }, [finishLogin])

  const respondToMfa = useCallback(async (code: string) => {
    setError(null)
    const respond = (supabase.auth as unknown as {
      respondToMfa?: (code: string) => Promise<{ data: { user: User | null }; error: AuthError | null }>
    }).respondToMfa
    if (!respond) return { error: { message: 'MFA not supported', name: 'AuthError', status: 400 } as AuthError }
    const { data, error } = await respond(code)
    if (error) { setError(error); return { error } }
    if (data.user) return await finishLogin(data.user)
    return { error: null }
  }, [finishLogin])

  const signUp = useCallback(async (email: string, password: string, fullName?: string, orgId?: string, phone?: string) => {
    setError(null)
    const metadata: Record<string, string> = { full_name: fullName || '', terms_accepted: 'true' }
    if (orgId) metadata.primary_org_id = orgId
    if (phone) metadata.phone = phone
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: `${window.location.origin}/signin`,
      },
    })
    if (error) {
      setError(error)
    } else {
      void supabase.functions.invoke('log-auth-email', {
        body: { type: 'signup_confirm', recipient_email: email },
      })
    }
    return { error }
  }, [])

  const signOut = useCallback(async () => {
    await performLocalSignOut()
  }, [performLocalSignOut])

  // Allow pages to refresh profile after saving changes
  const refreshProfile = useCallback(async () => {
    if (user) {
      await loadProfile(user.id)
    }
  }, [user, loadProfile])

  const clearError = useCallback(() => setError(null), [])

  useEffect(() => {
    sessionWarningOpenRef.current = sessionWarningOpen
  }, [sessionWarningOpen])

  useEffect(() => {
    if (!user?.id) {
      clearSessionTracking(activeSessionUserRef.current)
      activeSessionUserRef.current = null
      lastActivityWriteRef.current = 0
      clearSessionTimers()
      sessionWarningOpenRef.current = false
      setSessionWarningOpen(false)
      setSessionSecondsRemaining(Math.ceil(SESSION_WARNING_MS / 1000))
      return
    }

    activeSessionUserRef.current = user.id

    const now = Date.now()
    const sessionStartedAtKey = getSessionStartedAtKey(user.id)
    const lastActivityAtKey = getLastActivityAtKey(user.id)

    purgeStaleSessionKeys(localStorage, now)

    const storedSessionStartedAt = readStoredTimestamp(localStorage, sessionStartedAtKey)
    const storedLastActivityAt = readStoredTimestamp(localStorage, lastActivityAtKey)
    const reuseExisting =
      storedSessionStartedAt != null
      && storedLastActivityAt != null
      && now - storedSessionStartedAt < ABSOLUTE_TIMEOUT_MS
      && now - storedLastActivityAt < IDLE_TIMEOUT_MS
    const sessionStartedAt = reuseExisting ? storedSessionStartedAt : now
    const lastActivityAt = reuseExisting ? storedLastActivityAt : now

    localStorage.setItem(sessionStartedAtKey, String(sessionStartedAt))
    localStorage.setItem(lastActivityAtKey, String(lastActivityAt))
    lastActivityWriteRef.current = lastActivityAt

    scheduleSessionEnforcement(user.id)

    const activityEvents: Array<keyof WindowEventMap> = ['click', 'keydown', 'mousedown', 'mousemove', 'scroll', 'touchstart']
    const handleActivity = () => {
      recordActivity()
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !sessionWarningOpenRef.current) {
        recordActivity(true)
      }
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== sessionStartedAtKey && event.key !== lastActivityAtKey) return

      if (sessionWarningOpenRef.current) {
        syncWarningDeadline(user.id)
      } else {
        scheduleSessionEnforcement(user.id)
      }
    }

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true })
    })
    window.addEventListener('focus', handleActivity)
    window.addEventListener('storage', handleStorage)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity)
      })
      window.removeEventListener('focus', handleActivity)
      window.removeEventListener('storage', handleStorage)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearSessionTimers()
    }
  }, [clearSessionTimers, clearSessionTracking, recordActivity, scheduleSessionEnforcement, syncWarningDeadline, user?.id])

  return (
    <AuthContext.Provider value={{ user, profile, loading, profileLoading, error, signIn, respondToMfa, signUp, signOut, refreshProfile, clearError }}>
      {children}
      {user && sessionWarningOpen && (
        <SessionTimeoutWarning
          secondsRemaining={sessionSecondsRemaining}
          onContinue={() => recordActivity(true)}
          onSignOut={() => {
            void signOut()
          }}
        />
      )}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthContext() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider')
  return ctx
}

/* ── Login event recording (fire-and-forget) ──────── */

function recordLoginEvent(userId: string) {
  const ua = navigator.userAgent
  const browser = parseUserAgentBrowser(ua)
  const os = parseUserAgentOS(ua)
  const isTablet = /Tablet|iPad/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))
  const device_type = isTablet ? 'tablet' : /Mobi|Android/i.test(ua) ? 'mobile' : 'desktop'

  const loginRecord: Record<string, unknown> = {
    user_id: userId,
    login_method: 'email',
    success: true,
    user_agent: ua,
    browser,
    os,
    device_type,
  }

  fetch('https://ipapi.co/json/', { signal: typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(3000) : undefined })
    .then(r => r.json())
    .then(geo => {
      loginRecord.ip_address = geo.ip || null
      loginRecord.city = geo.city || null
      loginRecord.region = geo.region || null
      loginRecord.country = geo.country_name || null
    })
    .catch(() => {})
    .finally(() => {
      supabase.from('login_events').insert(loginRecord).then(() => {})
    })
}

function parseUserAgentBrowser(ua: string): string {
  if (ua.includes('EdgiOS/') || ua.includes('EdgA/') || ua.includes('Edg/')) return 'Edge'
  if (ua.includes('FxiOS/') || ua.includes('Firefox/')) return 'Firefox'
  if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera'
  if (ua.includes('CriOS/') || ua.includes('Chrome/')) return 'Chrome'
  if (ua.includes('Safari/')) return 'Safari'
  return 'Other'
}

function parseUserAgentOS(ua: string): string {
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS'
  if (ua.includes('Windows')) return 'Windows'
  if (ua.includes('Mac OS X') || ua.includes('Macintosh')) return 'macOS'
  if (ua.includes('Android')) return 'Android'
  if (ua.includes('Linux')) return 'Linux'
  if (ua.includes('CrOS')) return 'ChromeOS'
  return 'Other'
}
